import { Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

export type ApiEnvironmentKey = 'local' | 'development' | 'staging' | 'production';

interface ApiEnvironmentOption {
  key: ApiEnvironmentKey;
  label: string;
  baseUrl: string;
}

const STORAGE_KEY = 'gmcore.apiEnvironment';

/**
 * URLs de cada ambiente da API, hardcoded pra não precisar bundlar todos os
 * arquivos de environment num único build. O componente que consome esse
 * service só fica ativo quando o próprio ambiente montado é `local` — ou
 * seja, os builds de dev/staging/prod nunca expõem o seletor.
 */
const OPTIONS: readonly ApiEnvironmentOption[] = [
  { key: 'local',       label: 'Local (localhost)',   baseUrl: 'http://localhost:5188' },
  { key: 'development', label: 'API de desenvolvimento', baseUrl: 'https://api-dev.gmcore.gmtools.com.br' },
  { key: 'staging',     label: 'API de homologação', baseUrl: 'https://api-staging.gmcore.gmtools.com.br' },
  { key: 'production',  label: 'API de produção',    baseUrl: 'https://api.gmcore.gmtools.com.br' },
] as const;

/**
 * Permite trocar a base URL da API em runtime, sem rebuild. Usado só em
 * ambiente local — o toggle no header some em builds de dev/staging/prod.
 * A escolha persiste em `localStorage` e o `ApiEnvironmentInterceptor`
 * intercepta cada request pra reescrever a URL antes do envio.
 */
@Injectable({ providedIn: 'root' })
export class ApiEnvironmentService {
  public readonly options = OPTIONS;

  /** Signal reativo pro seletor renderizar o valor atual. */
  public readonly current = signal<ApiEnvironmentKey>(this.readFromStorage());

  public getCurrentBaseUrl(): string {
    return this.getBaseUrlFor(this.current());
  }

  public getBaseUrlFor(key: ApiEnvironmentKey): string {
    const found = OPTIONS.find((o) => o.key === key);
    return found ? found.baseUrl : environment.api.baseUrl;
  }

  /** Trocar de ambiente força reload — a maioria dos serviços só lê a URL
   *  na hora de montar a request, mas assumir estado limpo evita cache/estado
   *  cruzado com o ambiente anterior (ex.: tokens, telas em memória). */
  public set(key: ApiEnvironmentKey): void {
    this.current.set(key);
    this.writeToStorage(key);
  }

  /**
   * O toggle no header só aparece quando o app está sendo servido a partir
   * de `localhost`. Não amarra ao environmentTag do build (que costuma vir
   * como `development` mesmo com `ng serve` sem `--configuration=local`),
   * porque queremos que qualquer dev com `ng serve` veja o seletor. Deploy
   * real (build de dev/staging/prod hospedado em domínio externo) some.
   */
  public canSwitch(): boolean {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '::1';
  }

  private readFromStorage(): ApiEnvironmentKey {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === 'local' || raw === 'development' || raw === 'staging' || raw === 'production') {
        return raw;
      }
    } catch {
      // localStorage indisponível
    }
    return 'development';
  }

  private writeToStorage(value: ApiEnvironmentKey): void {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // ignora — modo anônimo ou storage bloqueado
    }
  }
}
